import React from "react";
import { motion } from "framer-motion";
import { Wrench, Repeat, Server, Check, ArrowUpRight } from "lucide-react";
import { SITE } from "@/constants/testIds";

const SERVICES = [
  {
    id: "custom",
    testId: SITE.serviceCustom,
    tag: "01 / Custom",
    title: "Custom Websites",
    desc:
      "Tailored sites for startups and growing businesses — clean design, strong performance, built to scale.",
    features: [
      "Bespoke design & branding",
      "Fast, responsive, accessible",
      "SEO-ready out of the box",
    ],
    icon: Wrench,
    accent: "#E9298A",
    href: "#contact",
  },
  {
    id: "waas",
    testId: SITE.serviceWaas,
    tag: "02 / Subscription",
    title: "Website as a Service",
    desc:
      "Our most popular option. No big upfront cost. We build, host, secure, and maintain your site for one simple monthly fee. Perfect for busy teams worldwide.",
    features: [
      "Build + host + maintain",
      "Unlimited small updates",
      "Cancel anytime",
    ],
    icon: Repeat,
    accent: "#FFE600",
    highlight: true,
    href: "#contact",
  },
  {
    id: "hosting",
    testId: SITE.serviceHosting,
    tag: "03 / Ops",
    title: "Hosting & Maintenance",
    desc:
      "Already have a site? We'll keep it fast, safe, and up to date — so you can stop worrying about tech gremlins.",
    features: [
      "Fast, monitored hosting",
      "Security & backups",
      "Monthly updates & fixes",
    ],
    icon: Server,
    accent: "#1A1A1A",
    href: "#contact",
  },
];

export default function Services() {
  return (
    <section id="services" className="relative py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <div className="max-w-3xl">
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl md:text-6xl lg:text-[76px] leading-[0.95] text-[#1A1A1A]">
              What we do{" "}
              <span className="relative inline-block">
                <span className="relative z-10">(and do well)</span>
                <span className="absolute left-0 right-0 bottom-1 md:bottom-2 h-3 md:h-4 lg:h-5 bg-[#FFE600] -z-0 -skew-y-1" />
              </span>
              .
            </h2>
            <p className="mt-6 text-lg md:text-xl text-[#1A1A1A]/70 max-w-2xl leading-relaxed">
              Three ways to work together. All friendly, all human. All designed to make
              your business look great and grow.
            </p>
          </div>
        </div>

        <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {SERVICES.map((s, idx) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.id}
                data-testid={s.testId}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, delay: idx * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className={`tb-service-card flex flex-col ${s.highlight ? "bg-[#FFE600]" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#1A1A1A]/70">
                    {s.tag}
                  </span>
                  <span
                    className="w-11 h-11 rounded-full flex items-center justify-center border border-[#1A1A1A]"
                    style={{ background: s.highlight ? "#1A1A1A" : s.accent, color: s.highlight ? s.accent : "#fff" }}
                  >
                    <Icon className="w-5 h-5" />
                  </span>
                </div>

                <h3 className="mt-8 font-display font-black text-3xl md:text-[34px] leading-[1.05] tracking-tight text-[#1A1A1A]">
                  {s.title}
                </h3>
                <p className="mt-4 text-[#1A1A1A]/75 leading-relaxed">
                  {s.desc}
                </p>

                <ul className="mt-6 space-y-3">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm md:text-[15px]">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-[#1A1A1A] text-white flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={s.href}
                  onClick={(e) => {
                    e.preventDefault();
                    const el = document.querySelector(s.href);
                    if (window.__lenis && el) window.__lenis.scrollTo(el, { offset: -60, duration: 1.2 });
                    else el && el.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="mt-8 inline-flex items-center gap-2 font-semibold text-[#1A1A1A] group"
                >
                  {s.id === "waas" ? "Start your subscription" : s.id === "custom" ? "Get a quote" : "Get in touch"}
                  <span className="w-8 h-8 rounded-full border border-[#1A1A1A] flex items-center justify-center group-hover:bg-[#1A1A1A] group-hover:text-white transition-colors">
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                </a>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
